import {
  extractMustUpdateQualificationFromInvestorProfile,
  getInvestorProfileProxyRecord,
} from '@shared/services/investor-profile/investor-profile.service'
import { useCurrentUser } from '@shared/stores/current-user'
import { createGlobalState } from '@vueuse/core'
import type { AppContext, PedagogicalContent } from 'src/global-types'
import { MX_CLASSICAL_GAUGE_MAX_AGE } from 'src/tpbp-core/models/common.const'
import type { InvestorProfileLabel } from 'src/tpbp-core/models/investor-profile/investor-profile.types'
import { InvestorProfileApiToCoreMinorLabelMapper } from 'src/tpbp-json-api-handler/investor-profile/investor-profile.mapper'
import type { RiskProfileModel } from 'src/tpbp-json-api-handler/models/models'
import { logger } from 'src/utils/logger.utils'
import { computed, ref, shallowRef } from 'vue'

export const useGlobalState = createGlobalState(() => {
  const { temporarilyFullAccessCurrentUser, currentUserId } = useCurrentUser()

  const _investorProfile = shallowRef<RiskProfileModel>()
  const _mustUpdateQualification = ref(false)

  const showClassicalGauge = computed(
    () =>
      (temporarilyFullAccessCurrentUser.value?.age || 0) <
      MX_CLASSICAL_GAUGE_MAX_AGE,
  )
  const pedagogicalContents = ref<PedagogicalContent[]>([])
  const context = shallowRef<AppContext>({
    bypassJwtRopc: false,
    apiUrl: '',
    apiUrlAdvisingReport: '',
    apiUrlSelfcare: '',
    raygunApiKey: '',
    geoapifyKey: '',
    subscriptionLink: '',
  })

  function getPedagogicalContent(id: string): PedagogicalContent | undefined {
    const found = pedagogicalContents.value?.find(
      (content) => content.externalId === id,
    )
    if (!found) {
      logger.warn(`Pedagogical content with id ${id} not found`)
    }

    return found
  }

  async function refreshInvestorProfile(): Promise<void> {
    const investorProfile = await getInvestorProfileProxyRecord(
      currentUserId.value,
    )

    _investorProfile.value = investorProfile
    _mustUpdateQualification.value =
      extractMustUpdateQualificationFromInvestorProfile({
        mustUpdateQualification: investorProfile?.mustUpdateQualification,
      })
  }

  return {
    getPedagogicalContent,
    investorProfile: computed(() => _investorProfile.value),
    investorProfileLabel: computed(
      (): InvestorProfileLabel =>
        InvestorProfileApiToCoreMinorLabelMapper[
          _investorProfile.value?.piLabel || ''
        ],
    ),
    mustUpdateQualification: computed(() => _mustUpdateQualification.value),
    showClassicalGauge,
    pedagogicalContents,
    context,

    refreshInvestorProfile,
  }
})
